// LiveSyncDot — subtle "live" indicator that pulses while a sync is
// happening and shows "X min ago" when idle. Tap to expand into a small
// per-source list with relative timestamps.
//
// Data source: `last_health_sync_at` + `last_health_sync_source` on the
// user document plus any per-provider `last_sync_at` rows. Refreshes
// every 60s so the relative label drifts forward without a hard reload.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { CheckCircle2, RefreshCw, Plug } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
const SOURCE_LABELS = {
  apple_health: "Apple Health",
  apple_health_xml: "Apple Health (XML)",
  garmin: "Garmin",
  fitbit: "Fitbit",
  oura: "Oura",
  whoop: "Whoop",
  samsung_health: "Samsung",
  fitnessview: "FitnessView",
  picnic: "Picnic",
  manual: "Handmatig"
};
function relativeNL(iso) {
  if (!iso) return "nooit gesynced";
  const then = new Date(iso).getTime();
  if (!then || isNaN(then)) return "—";
  const diff = (Date.now() - then) / 1000;
  if (diff < 30) return "zojuist";
  if (diff < 90) return "1 min geleden";
  if (diff < 3600) return `${Math.round(diff / 60)} min geleden`;
  if (diff < 7200) return "1 uur geleden";
  if (diff < 86400) return `${Math.round(diff / 3600)} uur geleden`;
  return `${Math.round(diff / 86400)} dagen geleden`;
}
function relativeEN(iso) {
  if (!iso) return "never synced";
  const then = new Date(iso).getTime();
  if (!then || isNaN(then)) return "—";
  const diff = (Date.now() - then) / 1000;
  if (diff < 30) return "just now";
  if (diff < 90) return "1 min ago";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 7200) return "1 hour ago";
  if (diff < 86400) return `${Math.round(diff / 3600)} hours ago`;
  return `${Math.round(diff / 86400)} days ago`;
}
export default function LiveSyncDot() {
  const {
    t
  } = useTranslation();
  const {
    i18n
  } = useTranslation();
  const lang = i18n.language?.startsWith("nl") ? "nl" : "en";
  const rel = lang === "nl" ? relativeNL : relativeEN;
  const labels = lang === "nl" ? {
    lastSynced: "Laatst gesynced",
    noSource: "geen bron",
    panelTitle: "Live sync · per bron",
    noConn: "Nog geen wearable verbonden.",
    footer: "Live sync bij elke app-open. Garmin/Oura pollt elk half uur op de achtergrond.",
    syncBtn: "Sync nu",
    syncing: "Syncen…",
    samplesOk: n => `${n} samples gesynced`,
    allFresh: "Alles is al up-to-date.",
    triggered: n => `${n} bron(en) ge-trigger-sync.`,
    noExternal: "Geen externe bron om te syncen.",
    failed: "Sync mislukt.",
    smartOnly: "Werkt alleen in de iOS-app."
  } : {
    lastSynced: "Last synced",
    noSource: "no source",
    panelTitle: "Live sync · per source",
    noConn: "No wearable connected yet.",
    footer: "Live sync on every app-open. Garmin/Oura polls every 30 min in the background.",
    syncBtn: "Sync now",
    syncing: "Syncing…",
    samplesOk: n => `${n} samples synced`,
    allFresh: "Everything is already up-to-date.",
    triggered: n => `${n} source(s) sync-triggered.`,
    noExternal: "No external source to sync.",
    failed: "Sync failed.",
    smartOnly: "Only works in the iOS app."
  };
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [pulsing, setPulsing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [, force] = useState(0);
  const lastSyncRef = useRef(null);

  // Listen for the local foreground-sync event published by
  // NativeBootstrap so the dot pulses *the moment* HealthKit finishes,
  // not 30s later when the server poll catches up.
  useEffect(() => {
    const onSync = () => {
      setPulsing(true);
      setTimeout(() => setPulsing(false), 2400);
      // Refresh the per-source data right after to update labels.
      api.get("/integrations/list").then(({
        data
      }) => setData(data)).catch(() => {});
    };
    window.addEventListener("wyng:health-synced", onSync);
    return () => window.removeEventListener("wyng:health-synced", onSync);
  }, []);
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const {
          data
        } = await api.get("/integrations/list");
        if (cancel) return;
        setData(data);
        // Pulse animation when the most-recent sync timestamp changed
        // since the last refresh — gives "live" feeling.
        const latest = (data?.items || []).filter(i => i.connected).map(i => i.last_sync_at).filter(Boolean).sort().pop();
        if (latest && latest !== lastSyncRef.current) {
          if (lastSyncRef.current) {
            // not first load
            setPulsing(true);
            setTimeout(() => setPulsing(false), 2400);
          }
          lastSyncRef.current = latest;
        }
      } catch (e) {
        // Silent — dashboard already shows the rest of the data.
      }
    };
    load();
    // Poll every 30s so we catch the 5-min auto-sync ticks.
    const poll = setInterval(load, 30_000);
    // Force a re-render every minute so the relative label drifts.
    const ticker = setInterval(() => force(n => n + 1), 60_000);
    return () => {
      cancel = true;
      clearInterval(poll);
      clearInterval(ticker);
    };
  }, []);
  const sources = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter(i => i.connected).map(i => ({
      source: i.id,
      label: SOURCE_LABELS[i.id] || i.name || i.id,
      last_sync_at: i.last_sync_at
    })).sort((a, b) => (b.last_sync_at || "").localeCompare(a.last_sync_at || ""));
  }, [data]);
  const newest = sources[0]?.last_sync_at;
  const hasAny = sources.length > 0;
  const isNative = Capacitor.getPlatform() === "ios";
  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      if (isNative) {
        // Native: trigger the full HealthKit pull. NativeBootstrap's
        // event dispatch will then fire the pulse + refresh.
        const {
          syncHealthLast24h
        } = await import("@/native/bridge");
        const res = await syncHealthLast24h(api);
        window.dispatchEvent(new CustomEvent("wyng:health-synced", {
          detail: {
            at: Date.now(),
            synced: res?.synced || 0
          }
        }));
        toast.success(res?.synced ? labels.samplesOk(res.synced) : labels.allFresh);
      } else {
        // Web: no on-device HealthKit. Trigger a per-provider sync
        // for every connected wearable, then refresh the list.
        const list = await api.get("/integrations/list").catch(() => ({
          data: null
        }));
        const connected = (list?.data?.items || []).filter(i => i.connected && i.id !== "apple_health").map(i => i.id);
        await Promise.allSettled(connected.map(p => api.post(`/integrations/sync/${p}`)));
        const {
          data
        } = await api.get("/integrations/list");
        setData(data);
        toast.success(connected.length ? labels.triggered(connected.length) : labels.noExternal);
      }
    } catch (e) {
      console.error(e);
      toast.error(labels.failed);
    } finally {
      setSyncing(false);
    }
  };
  return <div className="relative" data-testid="live-sync-dot">
            <button onClick={() => setOpen(v => !v)} data-testid="live-sync-toggle" className="group flex items-center gap-1.5 text-[10px] font-medium
                           text-muted-foreground hover:text-foreground
                           transition px-1.5 py-0.5 -mx-1.5 rounded-md
                           hover:bg-secondary/40" aria-label={t("liveSyncDot.sync_status")}>
                <span className="relative flex items-center justify-center w-2 h-2">
                    {hasAny && <span className={`absolute inset-0 rounded-full ${pulsing ? "bg-[#FFD60A]/60 animate-ping" : ""}`} />}
                    <span className={`relative w-1.5 h-1.5 rounded-full ${hasAny ? "bg-[#FFD60A]" : "bg-muted-foreground/40"}`} />
                </span>
                <span className="tabular-nums" data-testid="live-sync-label">
                    {hasAny ? rel(newest) : labels.noSource}
                </span>
            </button>

            {open && <>
                    <button className="fixed inset-0 z-30 bg-transparent cursor-default" aria-label={t("liveSyncDot.sluiten")} onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-1.5 z-40 w-60
                                   bg-card border border-border/70 rounded-xl shadow-xl
                                   backdrop-blur-xl overflow-hidden" data-testid="live-sync-panel">
                        <div className="px-3 py-2 border-b border-border/50">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                {labels.panelTitle}
                            </p>
                        </div>
                        <ul className="divide-y divide-border/30 max-h-[55dvh] overflow-y-auto">
                            {!hasAny && <li className="px-3 py-3 text-xs text-muted-foreground flex items-center gap-2">
                                    <Plug className="w-3.5 h-3.5" />
                                    {labels.noConn}
                                </li>}
                            {sources.map(s => <li key={s.source} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`live-sync-row-${s.source}`}>
                                    <div className="min-w-0 flex items-center gap-2">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-[#FFD60A] shrink-0" />
                                        <span className="text-xs font-semibold truncate">
                                            {s.label}
                                        </span>
                                    </div>
                                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                                        {rel(s.last_sync_at)}
                                    </span>
                                </li>)}
                        </ul>
                        <div className="px-3 py-2 border-t border-border/50 flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 text-muted-foreground" />
                            <p className="text-[10px] text-muted-foreground leading-tight flex-1">
                                {labels.footer}
                            </p>
                        </div>
                        <button onClick={syncNow} disabled={syncing} data-testid="live-sync-now-btn" className="w-full text-[11px] font-bold uppercase tracking-wider
                                       py-2.5 border-t border-border/50
                                       text-[#FFD60A] hover:bg-[#FFD60A]/10
                                       transition disabled:opacity-50
                                       flex items-center justify-center gap-1.5">
                            <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
                            {syncing ? labels.syncing : labels.syncBtn}
                        </button>
                    </div>
                </>}
        </div>;
}